import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TravelCariSource } from '@polatkan/api/travel-cari';
import { trpc } from '@/lib/trpc';
import { Combobox } from '@/components/ui/combobox';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { showSuccessToast } from '@/hooks/use-mutation-with-toast';
import { accountErrorKey } from './account-form-model';

const NONE = '__none__';
export function TravelCariAccountAssignment({
  source,
  id,
  typeCode,
  currentAccountId,
  canEdit,
}: {
  source: TravelCariSource;
  id: string;
  typeCode: string;
  currentAccountId: string | null;
  canEdit: boolean;
}) {
  const { t } = useTranslation('travelCari');
  const { t: common } = useTranslation('common');
  const [value, setValue] = useState(currentAccountId ?? NONE);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    data: types,
    error: typesError,
    refetch: retryTypes,
  } = trpc.travelCariType.listDetailed.useQuery(undefined, {
    enabled: canEdit,
  });
  const {
    data: current,
    isLoading: loadingCurrent,
    error: currentError,
    refetch: retryCurrent,
  } = trpc.account.byId.useQuery(currentAccountId ?? '', {
    enabled: !!currentAccountId,
  });
  const type = types?.find((row) => row.code === typeCode);
  const pool = type?.assignedAccounts ?? [];
  const {
    data: chart,
    error: chartError,
    refetch: retryChart,
  } = trpc.account.list.useQuery(
    { page: 1, limit: 2000, isActive: true },
    { enabled: canEdit && !!types && !pool.length }
  );
  const choices = pool.length
    ? pool.filter((account) => account.isActive)
    : (chart?.accounts ?? []);
  const readError = typesError || currentError || chartError;
  const ready = !!types && (!!pool.length || !!chart) && !readError;
  const currentOutsidePool = current && !choices.some((account) => account.id === current.id);
  useEffect(() => {
    setValue(currentAccountId ?? NONE);
    setError(null);
  }, [currentAccountId]);
  const utils = trpc.useUtils();
  const assign = trpc.travelCariType.assignCariAccount.useMutation({
    onSuccess: () => {
      void utils.travelCari.invalidate();
      void utils.travelCariType.invalidate();
      setConfirm(false);
      setError(null);
      showSuccessToast(t('cariTypes.accountAssigned'));
    },
    onError: (error) => {
      if (error.message.startsWith('CONFIRM_REQUIRED')) setConfirm(true);
      else {
        const key = accountErrorKey(error);
        setError(
          t(`account.errors.${key === 'readOnly' || key === 'permission' ? key : 'assignment'}`)
        );
      }
    },
  });
  const save = (confirmed = false) =>
    assign.mutateAsync({ source, cariId: id, accountId: value === NONE ? null : value, confirmed });
  return (
    <section className="bg-card space-y-3 border p-5">
      <h2 className="font-semibold">{t('cariTypes.assignedAccount')}</h2>
      <p className="text-muted-foreground text-sm">
        {currentAccountId && loadingCurrent
          ? t('account.loading')
          : current
            ? `${current.code} · ${current.name}`
            : currentError
              ? t('account.loadFailed')
              : t('cariTypes.noAccount')}
      </p>
      {readError && (
        <div role="alert" className="space-y-2 text-sm">
          <p>{t('account.loadFailed')}</p>
          <Button
            variant="outline"
            onClick={() => {
              if (canEdit) {
                void retryTypes();
                if (!pool.length) void retryChart();
              }
              if (currentAccountId) void retryCurrent();
            }}
          >
            {t('account.retry')}
          </Button>
        </div>
      )}
      <p className="text-muted-foreground text-xs">{t('account.assignmentHint')}</p>
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <div className="min-w-0 flex-1">
            <Combobox
              ariaLabel={t('cariTypes.assignedAccount')}
              disabled={assign.isPending || !ready}
              options={[
                { value: NONE, label: t('cariTypes.noAccount') },
                ...(currentOutsidePool
                  ? [
                      {
                        value: current.id,
                        label: `${current.code} · ${current.name}`,
                        disabled: true,
                      },
                    ]
                  : []),
                ...choices.map((account) => ({
                  value: account.id,
                  label: `${account.code} · ${account.name}`,
                })),
              ]}
              value={value}
              onValueChange={(value) => {
                setValue(value);
                setError(null);
              }}
            />
          </div>
          <Button
            disabled={assign.isPending || value === (currentAccountId ?? NONE) || !ready}
            onClick={() => {
              void save().catch(() => {});
            }}
          >
            {common('save')}
          </Button>
        </div>
      )}
      {error && (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      )}
      <ConfirmationDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={t('cariTypes.confirmReassignTitle')}
        error={error}
        description={t('cariTypes.confirmReassign')}
        confirmLabel={t('cariTypes.confirmReassignAction')}
        isLoading={assign.isPending}
        onConfirm={async () => {
          await save(true);
        }}
      />
    </section>
  );
}
